import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { min } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'

import { useScreenSizeOnly } from 'hooks/useScreenSize'
import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { OfferDurationOption } from '../DurationTabs/OfferDurationTabs'
import OfferDurationOptions from './OfferDurationOptions'
import OfferDurationSelectBox from './OfferDurationSelectBox'

interface Props {
  open: boolean
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  onSelect?: () => void
  onClose?: () => void
  durationOptions: Array<OfferDurationOption>
  className?: string
  onDurationSelect?: () => void
}

function OfferDurationSelect(props: Props) {
  const { open, durationOptions, onClick, onSelect = noop, onClose = noop, className, onDurationSelect } = props
  const intl = useIntl()
  const triggerRef = useRef<HTMLDivElement>(null)

  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const isMobile = useScreenSizeOnly('mobile')

  const onApplyClose = useCallback(() => {
    dispatch({ type: OfferPageActions.closeFilter })
    trackEvent('DURATION_FILTER_CLOSED')
    onClose()
  }, [dispatch, trackEvent, onClose])

  const onContinue = useCallback(() => {
    dispatch({ type: OfferPageActions.filterContinue })
    trackEvent('DURATION_FILTER_CLOSED')
    onSelect()
  }, [dispatch, trackEvent, onSelect])

  const minimumDuration = useMemo(() => min(durationOptions, option => option.duration)?.duration ?? 1, [durationOptions])

  const minStaySubtitle = minimumDuration > 1 ? intl.formatMessage(
    {
      id: '/mMZ6kBcn/',
      defaultMessage: 'Minimum stay for this offer is {minStay, plural, one {# night} other {# nights}}',
      description: 'Subtitle showing minimum stay duration for the offer',
    },
    { minStay: minimumDuration },
  ) : undefined

  const handleApply = useCallback(() => {
    onContinue()
    onDurationSelect?.()
  }, [onContinue, onDurationSelect])

  return <div className={className}>
    <OfferDurationSelectBox
      ref={triggerRef}
      onClick={onClick}
    />
    <DropdownList
      size="M"
      open={open && !isMobile}
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      onClose={onApplyClose}
      title={intl.formatMessage({
        id: 'bGee/nnKz3',
        defaultMessage: 'How long do you want to stay?',
        description: 'Title for duration selection dropdown on offer page',
      })}
      subtitle={minStaySubtitle}
      isPrimaryActionHidden={!!onDurationSelect}
      primaryActionProps={{
        children: intl.formatMessage({
          id: 'vmLmHA9swA',
          defaultMessage: 'Apply',
          description: 'Button text to apply selected duration on offer page',
        }),
        onClick: handleApply,
      }}
    >
      <OfferDurationOptions onSelect={onDurationSelect} />
    </DropdownList>
  </div>
}

export default withOfferPageAndReduxState((state, appState) => ({
  open: state.activeFilter === 'duration',
  durationOptions: getDurationOptions(state, appState),
}), OfferDurationSelect)
